import { useSelector } from 'react-redux';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import invariant from 'tiny-invariant';

import type { UserData, UserRole, UUID } from '@cobliteam/common-types';

import {
  type DefaultRole,
  PasswordRequester,
  type UserAttributes,
  type UserPreferences,
  type UserPreferencesToSave,
  UserRequester,
} from '@cobliteam/api-lib';
import GraphQLUserRequester from '@cobliteam/api-lib/src/dashboard/user/GraphQLUserRequester';

import { useUpdateOnboardingStore } from '@cobliteam/onboarding';
import { Environment } from '@cobliteam/react-environments';

import type { AppState } from '@cobliteam/store';

import useLocalStorage from '../../useLocalStorage';

const env = Environment.getInstance().getEnvironment();
const userRequester = new UserRequester({ host: env.herbieApiHost });
const userRequesterGraphQl = new GraphQLUserRequester();
const passwordRequester = new PasswordRequester({ host: env.herbieApiHost });

export const QUERY_KEY = 'USER';
export const QUERY_KEY_USER_DATA = 'USER_DATA';
export const QUERY_KEY_USER_PREFERENCES = 'USER_PREFERENCES';

interface UserPreferencesCreateVariables {
  userId: UUID;
  preferencesToSave: UserPreferencesToSave;
}

interface UserPreferencesUpdateVariables {
  userId: UUID;
  preferencesToUpdate: UserPreferences;
}

interface UserPreferencesUpdateContext {
  previousPreferences?: UserPreferences;
}

interface UserPermissionsUpdateVariables {
  userId: UUID;
  role: UserRole;
  groupsRestriction: UUID[];
  customPermissions: string[];
}

interface PasswordUpdateVariables {
  email: string;
  oldPassword: string;
  newPassword: string;
}

const useUser = () => {
  const queryClient = useQueryClient();
  const session = useSelector((state: AppState) => state.login.session);
  const user = session?.user;

  const [, setUserAttributes] =
    useLocalStorage<UserAttributes>('userAttributes');

  const { setOnboardingStore, setOnboardingStepsStore } =
    useUpdateOnboardingStore();

  const getSession = () => {
    invariant(session, 'Session is not defined');
    return session;
  };

  const getUser = () => {
    invariant(user, 'User is not defined');
    return user;
  };

  const useGetUserData = (userId?: UUID, enabled = true) => {
    const userIdLogged = enabled ? userId || getUser().id : '';
    return useQuery<UserData>({
      queryKey: [QUERY_KEY, QUERY_KEY_USER_DATA],
      queryFn: () => userRequesterGraphQl.getUserData({ userId: userIdLogged }),
      enabled,
      staleTime: 1000 * 60 * 5, // 5 minutes,
      onSuccess: (userData) => {
        setOnboardingStore(userData?.userPreferences?.onboarding);
        setOnboardingStepsStore(userData?.userPreferences?.onboarding?.steps);
        setUserAttributes(userData?.userAttributes);
      },
    });
  };

  const getDefaultRoles = (enabled = true) => {
    return useQuery<DefaultRole[]>({
      queryKey: [QUERY_KEY, 'DEFAULT_ROLES'],
      queryFn: () => userRequester.getDefaultRoles(),
      enabled,
      staleTime: Number.POSITIVE_INFINITY,
    });
  };

  const getPreferences = (userId?: UUID, enabled = true) => {
    const userIdLogged = enabled ? userId || getUser().id : '';

    return useQuery<UserPreferences | undefined>({
      queryKey: [QUERY_KEY, QUERY_KEY_USER_PREFERENCES, userIdLogged],
      queryFn: () => userRequester.getUserPreferences(userIdLogged),
      enabled,
      staleTime: Number.POSITIVE_INFINITY,
    });
  };

  const updatePermissions = useMutation<
    void,
    unknown,
    UserPermissionsUpdateVariables
  >({
    mutationFn: ({ userId, role, groupsRestriction, customPermissions }) =>
      userRequester.saveUserPermissions(
        userId,
        role,
        groupsRestriction,
        customPermissions
      ),
  });

  const updatePassword = useMutation<void, unknown, PasswordUpdateVariables>({
    mutationFn: ({ email, oldPassword, newPassword }) =>
      passwordRequester.changePassword(email, oldPassword, newPassword),
  });

  const createPreferences = useMutation<
    UUID,
    unknown,
    UserPreferencesCreateVariables
  >({
    mutationFn: (data) =>
      userRequester.saveUserPreferences(data.userId, data.preferencesToSave),
    onMutate: async (data) => {
      const { userId } = data;

      queryClient.cancelQueries([
        QUERY_KEY,
        QUERY_KEY_USER_PREFERENCES,
        userId,
      ]);

      const previousTodos = queryClient.getQueryData([
        QUERY_KEY,
        QUERY_KEY_USER_PREFERENCES,
        userId,
      ]);

      const createdUserPreferences: UserPreferences = {
        id: userId,
        vehicleGroupIds: data.preferencesToSave.vehicleGroupIds,
        useVehiclesCluster: data.preferencesToSave.useVehiclesCluster,
      };

      queryClient.setQueryData(
        [QUERY_KEY, QUERY_KEY_USER_PREFERENCES, userId],
        createdUserPreferences
      );

      return { previousTodos };
    },
    onError: (err, data, context: any) => {
      const { userId } = data;
      queryClient.setQueryData(
        [QUERY_KEY, QUERY_KEY_USER_PREFERENCES, userId],
        context?.previousTodos
      );
    },
    onSettled: (newTodo, error, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, QUERY_KEY_USER_DATA],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, QUERY_KEY_USER_PREFERENCES, variables.userId],
      });
    },
  });

  const updatePreferences = useMutation<
    void,
    unknown,
    UserPreferencesUpdateVariables,
    UserPreferencesUpdateContext
  >({
    mutationFn: (variables) =>
      userRequester.updateUserPreferences(
        variables.userId,
        variables.preferencesToUpdate
      ),
    onMutate: (variables) => {
      const { userId } = variables;

      const previousPreferences: UserPreferences | undefined =
        queryClient.getQueryData([
          QUERY_KEY,
          QUERY_KEY_USER_PREFERENCES,
          userId,
        ]);

      // update userData
      const userData: any = queryClient.getQueryData([
        QUERY_KEY,
        QUERY_KEY_USER_DATA,
      ]);
      queryClient.setQueryData([QUERY_KEY, QUERY_KEY_USER_DATA], {
        ...userData,
        userPreferences: variables.preferencesToUpdate,
      });

      // update preferences
      queryClient.cancelQueries([
        QUERY_KEY,
        QUERY_KEY_USER_PREFERENCES,
        userId,
      ]);
      queryClient.setQueryData(
        [QUERY_KEY, QUERY_KEY_USER_PREFERENCES, userId],
        variables.preferencesToUpdate
      );

      return { previousPreferences };
    },
    onError: (_, variables, context) => {
      if (context?.previousPreferences) {
        queryClient.setQueryData(
          [QUERY_KEY, QUERY_KEY_USER_PREFERENCES, variables.userId],
          context.previousPreferences
        );
      } else {
        queryClient.invalidateQueries([
          QUERY_KEY,
          QUERY_KEY_USER_PREFERENCES,
          variables.userId,
        ]);
      }
    },
  });

  return {
    QUERY_KEY_USER_DATA,
    QUERY_KEY,
    user,
    session,
    getUser,
    useGetUserData,
    getSession,
    getPreferences,
    getDefaultRoles,
    updatePassword,
    createPreferences,
    updatePreferences,
    updatePermissions,
  };
};

export default useUser;
